import React, { useEffect, useRef } from 'react'
import { useMutation } from 'react-query'
import { DateTime } from 'luxon'

import { BASE_URL } from '@configs/config'
import { useStoryReader } from '@hooks/book/useStoryReader'
import { UserActionEnum } from '@interfaces/UserActionEnum'
import { UserActionLogType } from '@models/userActionLog/UserActionLogType'
import { useUserActionLogAction } from '@hooks/userActionLog/useUserActionLogAction'
import { useAuthentication } from '@hooks/useAuthentication'
import { UserActionReadStateEnum } from '@interfaces/UserActionReadStateEnum'
import { StoryPageProps } from './interface'
import { ErrorMessage } from '@components/ErrorMessage'

function withStoryPage(Component: React.FC<StoryPageProps>) {
  function StoryPage() {
    const { user } = useAuthentication()
    const { data, canAction, bookId, isOwner, errorMessage } = useStoryReader()
    const userActionLogClient = useUserActionLogAction()
    const canCreateUserActionLog = useRef(false)
    canCreateUserActionLog.current = canAction && !isOwner && !user.isAdmin

    const { mutate: createUserActionLog } = useMutation(
      (userActionLog: UserActionLogType) =>
        userActionLogClient.createUserActionLog(userActionLog)
    )

    useEffect(() => {
      const startTime = DateTime.now()
      return () => {
        if (canCreateUserActionLog.current) {
          createUserActionLog({
            bookId,
            time: Math.abs(startTime.diffNow('milliseconds').milliseconds),
            action: UserActionEnum.READ,
            readState: UserActionReadStateEnum.STORY,
          })
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    if (errorMessage) {
      return (
        <ErrorMessage
          message={
            errorMessage === `Book #${bookId} not found`
              ? 'เรื่องนี้ถูกปิดการเผยแพร่แล้ว'
              : errorMessage
          }
          redirectUrl='/'
        />
      )
    }
    if (!data) return null

    const componentProps = {
      shareUrl: `${BASE_URL}/story/${data?.id}`,
      data,
      errorMessage,
    }

    return <Component {...componentProps} />
  }

  return StoryPage
}

export { withStoryPage }
